import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { DownloadJob } from 'switch-fe-shared/components/Downloads/DownloadsTray';
import { HttpRequestOptions } from 'switch-fe-shared/utils/http.service';
import { Job } from 'switch-shared/modules/crud/job/job.entity';
import { Report } from 'switch-shared/modules/crud/report/report.entity';

import { apiClient } from '../../lib/axiosWrapper';
import { ENDPOINT_URLS } from '../switch-rest-api/config/apiTags';

/** Helper to create toast-based request options */
const createToastOptions = (
  pendingMessage: string,
  successMessage: string = 'Success'
): HttpRequestOptions => ({
  onPending: () => toast.info(pendingMessage, { autoClose: 10000 }),
  onSuccess: () => {
    toast.dismiss();
    toast.success(successMessage, { autoClose: 10000 });
  },
});

export class FileAPIService {
  public async fetchCSV(
    endpoint: string,
    params: any,
    fileName: string
  ): Promise<void> {
    const apiResult = await apiClient.getWithParams<any>(
      `${endpoint}/searchAsCsv`,
      params,
      { responseType: 'arraybuffer' },
      createToastOptions('Fetching CSV')
    );

    const blob = new Blob([apiResult.data], { type: 'application/zip' });

    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.download = `${fileName}.csv`;

    document.body.appendChild(downloadLink);
    downloadLink.click();

    document.body.removeChild(downloadLink);
  }

  public async fetchZIP(
    endpoint: string,
    params: any,
    fileName: string
  ): Promise<void> {
    const res = await apiClient.getWithParams<Blob>(
      endpoint,
      params,
      {
        responseType: 'blob',
      },
      createToastOptions('Fetching ZIP')
    );

    const link = document.createElement('a');
    const url = URL.createObjectURL(res.data);
    link.href = url;
    link.download = `${fileName}.zip`;
    link.click();
  }

  /** Downloads the files of the complete reports among `reportIds` as one ZIP named after `title` and the current time. */
  public async fetchReportZip(reportIds: string[], title: string) {
    await this.fetchZIP(
      `${ENDPOINT_URLS.REPORT}/fetchReportZip`,
      { reportIds },
      `${title} ${dayjs().format('DD/MM/YYYY HH:mm')}`
    );
  }

  public async fetchSignedURLs(endpoint: string, params: any): Promise<any> {
    const apiResult = await apiClient.getWithParams<any>(`${endpoint}`, params);
    return apiResult;
  }

  /**
   * Fetches a download URL for the report's file.
   */
  public async downloadReportFile(
    report: Report,
    fileType?: 'pdf' | 'excel'
  ): Promise<{ data: string }> {
    return await apiClient.getWithParams(
      `${ENDPOINT_URLS.REPORT}/downloadReport`,
      {
        reportId: report._id,
        fileType,
      }
    );
  }

  /**
   * Fetches the report file directly as a blob.
   */
  public async fetchReportFile(
    report: Report,
    fileType: 'pdf' | 'excel'
  ): Promise<Blob> {
    const response = await apiClient.getWithParams(
      `${ENDPOINT_URLS.REPORT}/fetchReport`,
      {
        reportId: report._id,
        fileType,
      },
      {
        responseType: 'blob',
      },
      createToastOptions(
        `Downloading ${fileType === 'pdf' ? 'PDF' : 'Excel'}...`
      )
    );
    return response.data;
  }

  /**
   * Fetches the fee methodology workbook for a report as a blob.
   */
  public async fetchMethodologyFile(report: Report): Promise<Blob> {
    const response = await apiClient.getWithParams(
      `${ENDPOINT_URLS.REPORT}/fetchMethodology`,
      {
        reportId: report._id,
      },
      {
        responseType: 'blob',
      },
      createToastOptions('Downloading methodology...')
    );
    return response.data;
  }

  public async fetchXLSX(
    endpoint: string,
    params: any,
    fileName: string
  ): Promise<void> {
    const res = await apiClient.getWithParams(
      `${endpoint}/searchAsXlsx`,
      params,
      { responseType: 'arraybuffer' },
      createToastOptions('Fetching Excel')
    );

    const blob = new Blob([res.data], {
      type: 'application/zip',
    });

    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.download = `${fileName}.xlsx`;

    document.body.appendChild(downloadLink);
    downloadLink.click();

    // Clean up: remove the link from the body
    document.body.removeChild(downloadLink);
  }

  public async fetchManualReadingUpload(job: Job): Promise<void> {
    const res = await apiClient.getWithParams(
      'job/fetchManualReadingUpload',
      { jobId: job._id },
      { responseType: 'arraybuffer' },
      createToastOptions('Fetching file...')
    );

    const blob = new Blob([res.data], { type: 'text/csv' });

    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.download = job.metadata.filename;

    document.body.appendChild(downloadLink);
    downloadLink.click();

    // Clean up: remove the link from the body
    document.body.removeChild(downloadLink);
  }

  public async fetchDownload(job: DownloadJob): Promise<void> {
    // Using toast.loading which returns an ID for dismissal
    const t = toast.loading('Downloading...');

    const res = await apiClient.getWithParams(
      'job/fetchDownload',
      { jobId: job._id },
      { responseType: 'arraybuffer' },
      {
        onSuccess: () => {
          toast.dismiss(t);
          toast.success('Success', { autoClose: 5000 });
        },
        onError: () => {
          toast.dismiss(t);
        },
      }
    );

    const blob = new Blob([res.data], { type: job.metadata?.filetype });

    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.download = job.metadata?.filename ?? job._id;

    document.body.appendChild(downloadLink);
    downloadLink.click();

    document.body.removeChild(downloadLink);
  }

  public async fetchJobAs(
    job: Job,
    filetype: string,
    filename: string
  ): Promise<void> {
    const t = toast.loading('Downloading...');

    const res = await apiClient.getWithParams(
      'job/fetchAs',
      { jobId: job._id, filetype, filename },
      { responseType: 'arraybuffer' },
      {
        onSuccess: () => {
          toast.dismiss(t);
          toast.success('Success', { autoClose: 5000 });
        },
        onError: () => {
          toast.dismiss(t);
        },
      }
    );

    const blob = new Blob([res.data], { type: filetype });

    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(blob);
    downloadLink.download = filename;

    document.body.appendChild(downloadLink);
    downloadLink.click();

    document.body.removeChild(downloadLink);
  }
}
